import { motion } from 'framer-motion';
import { Play, ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getLocalizedPath } from '@/lib/routes';
import type { News as NewsType } from '../../shared/schema';

export const News = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';

  const { data: allNews = [], isLoading, isError, refetch } = useQuery<NewsType[]>({
    queryKey: ['/api/news'],
  });

  const getTitle = (n: NewsType) => lang === 'ar' ? n.titleAr : n.titleEn;
  const getExcerpt = (n: NewsType) => lang === 'ar' ? n.excerptAr : n.excerptEn;
  const getDate = (n: NewsType) => lang === 'ar' ? n.dateAr : n.dateEn;
  const getSlug = (n: NewsType) => lang === 'ar' ? n.slugAr : n.slugEn;

  const displayNews = allNews.slice(0, 3);
  const featuredNews = displayNews[0];
  const otherNews = displayNews.slice(1);

  return (
    <section id="news" className="py-24 lg:py-32 bg-background relative overflow-hidden">

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-tight text-foreground">
            <img src="/title-icon.png" alt="" className="w-8 h-8 md:w-10 md:h-10" />
            {t('news.label')}
          </div>
        </motion.div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <p className="text-muted-foreground">{t('common.loadError', 'Failed to load data')}</p>
            <button onClick={() => refetch()} data-testid="button-retry-news-section" className="px-6 py-2.5 rounded-full text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">{t('common.retry', 'Try Again')}</button>
          </div>
        ) : !featuredNews ? (
          <div className="text-center py-24 text-muted-foreground">
            {t('news.viewAll')}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8">
            <Link to={`${getLocalizedPath('media', lang)}/${getSlug(featuredNews)}`}>
              <motion.article
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6 }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="group cursor-pointer"
                data-testid={`card-featured-news-${featuredNews.id}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl mb-6 gradient-border">
                  <motion.img
                    src={featuredNews.image}
                    alt={getTitle(featuredNews)}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="absolute top-6 left-6 glass-card px-4 py-2 rounded-full"
                  >
                    <span className="text-sm text-foreground font-medium">{getDate(featuredNews)}</span>
                  </motion.div>

                  <div className="absolute bottom-8 left-8 right-8">
                    <h3 className="text-2xl md:text-3xl font-black text-foreground uppercase mb-4 group-hover:text-gradient transition-all duration-300">
                      {getTitle(featuredNews)}
                    </h3>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-wider">
                  <Play className="w-4 h-4" /> {t('news.readMore')}
                </span>
              </motion.article>
            </Link>

            <div className="flex flex-col gap-6">
              {otherNews.map((item, index) => (
                <Link key={item.id} to={`${getLocalizedPath('media', lang)}/${getSlug(item)}`}>
                  <motion.article
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.6, delay: (index + 1) * 0.15 }}
                    whileHover={{ x: 10, transition: { duration: 0.3 } }}
                    className="group cursor-pointer flex gap-6 glass-card p-4 rounded-xl hover-lift"
                    data-testid={`card-news-${item.id}`}
                  >
                    <div className="relative w-32 h-32 flex-shrink-0 overflow-hidden rounded-lg">
                      <motion.img
                        src={item.image}
                        alt={getTitle(item)}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                    <div className="flex flex-col justify-center">
                      <motion.span className="text-sm text-foreground font-medium mb-2">
                        {getDate(item)}
                      </motion.span>
                      <h3 className="text-lg font-bold text-foreground uppercase mb-2 group-hover:text-gradient transition-all duration-300 line-clamp-2">
                        {getTitle(item)}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-2">{getExcerpt(item)}</p>
                    </div>
                    <motion.div className="self-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-5 h-5 text-primary" />
                    </motion.div>
                  </motion.article>
                </Link>
              ))}
            </div>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-12"
        >
          <Link to={getLocalizedPath('media', lang)}>
            <motion.span
              className="btn-outline inline-block"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              {t('news.viewAll')}
            </motion.span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
